import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, InputBase, Typography } from "@mui/material";
import { ChevronLeft, Headset, Plus, Search, X } from "lucide-react";

import { Dialog } from "../../components/Dialog";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { Avatar } from "../../components/Avatar";
import { CustomSelector } from "../../components/CustomSelector";
import { UnreadThreadBadge } from "../../components/UnreadThreadBadge";
import SuccessPopup from "../../components/SuccessPopup";
import { useAuth } from "../../components/contexts/AuthContext";
import { getData } from "../../components/hooks/getData";
import { useTheme } from "../../theme/useTheme";
import { getAvatarImageUrl } from "../../utils/avatarUtils";
import { TICKET_TYPE_SUPPORT } from "../../utils/supportTickets";
import apiClient, { sendMessageWithAttachments } from "../../api-client";
import { ChatInterface } from "../Incidents/ChatInterface";
import { CreateIncidentModal } from "../Incidents/CreateIncidentModal";

const STATUS_OPTIONS = [
  { value: "new", label: "Nueva", color: "red" },
  { value: "open", label: "Abierta", color: "yellow" },
  { value: "closed", label: "Cerrada", color: "green" },
];
const STATUS_BY_VALUE = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o]));

const FILTERS = [
  { value: "all", label: "Todos" },
  { value: "new", label: "Nuevas" },
  { value: "open", label: "Abiertas" },
  { value: "closed", label: "Cerradas" },
];

const normalizeList = (payload) =>
  Array.isArray(payload) ? payload : Array.isArray(payload?.results) ? payload.results : [];

const timeAgo = (iso) => {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)}d`;
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });

const StatusBadge = ({ status, ...props }) => {
  const option = STATUS_BY_VALUE[status] || STATUS_BY_VALUE.new;
  return (
    <Badge color={option.color} {...props}>
      {option.label}
    </Badge>
  );
};

const HeaderField = ({ label, children, first = false }) => {
  const { neutrals } = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: { xs: "45%", md: 0 },
        px: { md: first ? 0 : 2 },
        pr: { xs: 2 },
        borderLeft: { md: first ? "none" : `1px solid ${neutrals.gray[200]}` },
      }}
    >
      <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      {children}
    </Box>
  );
};

const fieldValueSx = { fontSize: 14, fontWeight: 500, lineHeight: "24px" };

const TicketListItem = ({ ticket, selected, onSelect, showAgency }) => {
  const { theme, neutrals } = useTheme();
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onSelect(ticket.id)}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "block",
        width: "100%",
        cursor: "pointer",
        px: 2,
        py: 1.25,
        borderBottom: `1px solid ${neutrals.gray[200]}`,
        borderLeft: `3px solid ${selected ? theme.palette.primary.main : "transparent"}`,
        backgroundColor: selected ? `${theme.palette.primary.main}12` : "transparent",
        "&:hover": { backgroundColor: selected ? `${theme.palette.primary.main}12` : neutrals.gray[50] },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            color: selected ? theme.palette.primary.main : "text.primary",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ticket.title}
        </Typography>
        <UnreadThreadBadge count={ticket.unopened_unread_notifications} />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography
          color="text.secondary"
          sx={{ flex: 1, minWidth: 0, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {timeAgo(ticket.created_at)}
          {showAgency && ticket.agency_info?.name ? ` · ${ticket.agency_info.name}` : ""}
        </Typography>
        <StatusBadge status={ticket.status} dot={false} sx={{ py: "0.25rem", fontSize: "0.6875rem" }} />
      </Box>
    </Box>
  );
};

const TicketDetail = ({ ticketId, currentUser, interceptorReady, onBack, onTicketChanged, notify }) => {
  const { theme, neutrals } = useTheme();
  const isSuperAdmin = currentUser?.role === "superadmin";
  const [messagesRefresh, setMessagesRefresh] = useState(0);
  const [localMessages, setLocalMessages] = useState([]);
  const [localStatus, setLocalStatus] = useState(null);
  const [localAssignee, setLocalAssignee] = useState(undefined);

  const { data: ticket, isLoading } = getData(
    `/communication/tickets/${ticketId}/`,
    interceptorReady,
    0
  );
  const threadId = ticket?.thread?.id;
  const { data: threadMessages } = getData(
    threadId ? `/communication/threads/${threadId}/messages/all` : null,
    interceptorReady && !!threadId,
    messagesRefresh
  );
  const { data: superadminsResponse } = getData("/users/?role=superadmin", interceptorReady && isSuperAdmin, 0);

  useEffect(() => {
    setLocalMessages([]);
    setLocalStatus(null);
    setLocalAssignee(undefined);
  }, [ticketId]);

  useEffect(() => {
    if (threadMessages) setLocalMessages(threadMessages);
  }, [threadMessages]);

  useEffect(() => {
    const ids = ticket?.unopened_notification_ids;
    if (!ids?.length) return;
    apiClient
      .post("dashboard/notifications/mark-opened/", { ids })
      .then(() => onTicketChanged?.())
      .catch(() => {});
  }, [ticket?.id]);

  const assigneeOptions = useMemo(
    () =>
      normalizeList(superadminsResponse).map((user) => ({
        value: user.id,
        label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email,
      })),
    [superadminsResponse]
  );

  if (isLoading || !ticket) {
    return (
      <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const status = localStatus ?? ticket.status;
  const assigneeId = localAssignee !== undefined ? localAssignee : ticket.assigned_to_info?.id ?? null;
  const assigneeName =
    assigneeOptions.find((o) => o.value === assigneeId)?.label || ticket.assigned_to_info?.full_name;

  const patchTicket = async (payload, successMessage, rollback) => {
    try {
      await apiClient.patch(`/communication/tickets/${ticket.id}/`, payload);
      notify(successMessage);
      onTicketChanged?.();
      if ("status" in payload) setMessagesRefresh((prev) => prev + 1);
    } catch (error) {
      rollback();
      notify(error.response?.data?.detail || "No se pudo actualizar la consulta", true);
    }
  };

  const handleStatusChange = (nextStatus) => {
    if (nextStatus === status) return;
    const previous = localStatus;
    setLocalStatus(nextStatus);
    patchTicket({ status: nextStatus }, "Estado actualizado", () => setLocalStatus(previous));
  };

  const handleAssigneeChange = (nextAssignee) => {
    if (nextAssignee === assigneeId) return;
    const previous = localAssignee;
    setLocalAssignee(nextAssignee);
    patchTicket({ assigned_to: nextAssignee }, "Responsable actualizado", () => setLocalAssignee(previous));
  };

  const handleMessagesUpdate = (messageData, action = "add") => {
    if (action === "delete") {
      setLocalMessages((prev) => prev.filter((msg) => msg.id !== messageData.id));
    } else if (action === "update") {
      setLocalMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageData.id
            ? { ...msg, content: messageData.content, updated_at: messageData.updated_at }
            : msg
        )
      );
    } else {
      setLocalMessages((prev) => [...prev, messageData]);
    }
  };

  const creatorName = ticket.created_by?.full_name || "Usuario";

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 2, borderBottom: `1px solid ${neutrals.gray[200]}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, pr: 6, minHeight: 40 }}>
          <IconButton size="small" onClick={onBack} sx={{ display: { md: "none" } }} aria-label="Volver">
            <ChevronLeft size={18} />
          </IconButton>
          <Typography sx={{ fontSize: 18, fontWeight: 600, minWidth: 0, overflowWrap: "anywhere" }}>
            {ticket.title}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexWrap: { xs: "wrap", md: "nowrap" }, rowGap: 2 }}>
          <HeaderField label="Reportado por" first>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                name={creatorName}
                src={getAvatarImageUrl(ticket.created_by?.profile_image) || undefined}
                size="sm"
              />
              <Typography sx={fieldValueSx} noWrap>
                {creatorName}
              </Typography>
            </Box>
            {ticket.agency_info?.name && (
              <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }} noWrap>
                {ticket.agency_info.name}
              </Typography>
            )}
          </HeaderField>
          <HeaderField label="Fecha de reporte">
            <Typography sx={fieldValueSx}>{formatDate(ticket.created_at)}</Typography>
          </HeaderField>
          <HeaderField label="Responsable">
            {isSuperAdmin ? (
              <CustomSelector
                value={assigneeId ?? ""}
                onChange={handleAssigneeChange}
                options={assigneeOptions}
                placeholder="Sin asignar"
                size="sm"
                triggerVariant="minimal"
              />
            ) : (
              <Typography sx={{ ...fieldValueSx, color: assigneeName ? "text.primary" : "text.disabled" }}>
                {assigneeName || "Sin asignar"}
              </Typography>
            )}
          </HeaderField>
          <HeaderField label="Estado">
            {isSuperAdmin ? (
              <StatusBadge
                status={status}
                selector
                value={status}
                options={STATUS_OPTIONS}
                onChange={handleStatusChange}
              />
            ) : (
              <StatusBadge status={status} />
            )}
          </HeaderField>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ChatInterface
          currentUser={currentUser}
          customMessages={localMessages}
          threadId={threadId}
          participants={ticket.thread?.participants || []}
          onMessagesUpdate={handleMessagesUpdate}
          onMessageSent={() => setMessagesRefresh((prev) => prev + 1)}
          showThreadHeader={false}
          mentionsZIndex={9999}
          mentionsSuggestionsPortal
        />
      </Box>
    </Box>
  );
};

export function SupportModal({ open, onClose, initialTicketId = null, onUnreadChange }) {
  const { theme, neutrals } = useTheme();
  const { user: currentUser, interceptorReady } = useAuth();
  const isSuperAdmin = currentUser?.role === "superadmin";

  const [selectedId, setSelectedId] = useState(initialTicketId);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [listRefresh, setListRefresh] = useState(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [popup, setPopup] = useState({ open: false, message: "", error: false });

  useEffect(() => {
    if (open && initialTicketId) setSelectedId(initialTicketId);
  }, [open, initialTicketId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const listParams = new URLSearchParams({ ticket_type: TICKET_TYPE_SUPPORT, page_size: "100" });
  if (statusFilter !== "all") listParams.append("status", statusFilter);
  if (search) listParams.append("search", search);

  const { data: listResponse, isLoading: isLoadingList } = getData(
    `/communication/tickets/?${listParams.toString()}`,
    open && interceptorReady,
    listRefresh
  );
  const tickets = normalizeList(listResponse);
  const stats = listResponse?.status_stats || {};
  const totalCount = (stats.new || 0) + (stats.open || 0) + (stats.closed || 0);

  // En escritorio siempre hay una consulta abierta a la derecha
  const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 900px)").matches;
  useEffect(() => {
    if (open && !selectedId && isDesktop && tickets.length) setSelectedId(tickets[0].id);
  }, [open, tickets.length]);

  const notify = (message, error = false) => setPopup({ open: true, message, error });

  const refreshList = () => {
    setListRefresh((prev) => prev + 1);
    onUnreadChange?.();
  };

  // El ticket de soporte nace sin descripción: la descripción (y la imagen) es el primer mensaje del chat
  const handleCreate = async (formData) => {
    let ticket;
    try {
      ({ data: ticket } = await apiClient.post("/communication/tickets/", {
        ticket_type: TICKET_TYPE_SUPPORT,
        title: formData.title,
      }));
    } catch (error) {
      notify(error.response?.data?.detail || "No se pudo enviar la consulta", true);
      return;
    }
    try {
      await sendMessageWithAttachments(
        ticket.thread.id,
        formData.description,
        formData.ticket_image_files
      );
      notify("Consulta enviada. Te responderemos lo antes posible.");
    } catch {
      notify("Consulta creada, pero no se pudo enviar el mensaje. Escríbelo en el chat.", true);
    }
    setStatusFilter("all");
    setSearchInput("");
    setSelectedId(ticket.id);
    refreshList();
  };

  const filterLabel = (filter) => {
    const count = filter.value === "all" ? totalCount : stats[filter.value] || 0;
    return `${filter.label} (${count})`;
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "min(1400px, calc(100% - 32px))",
            height: "calc(100% - 64px)",
            maxHeight: "calc(100% - 32px)",
            maxWidth: "none",
            m: 2,
            borderRadius: "16px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "row",
          },
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Cerrar soporte"
          sx={{ position: "absolute", top: 20, right: 20, zIndex: 1 }}
        >
          <X size={20} />
        </IconButton>

        {/* Lista */}
        <Box
          sx={{
            width: { xs: "100%", md: 420 },
            flexShrink: 0,
            borderRight: `1px solid ${neutrals.gray[200]}`,
            display: { xs: selectedId ? "none" : "flex", md: "flex" },
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box sx={{ px: 2.5, pt: 3, pb: 2, borderBottom: `1px solid ${neutrals.gray[200]}` }}>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Headset size={20} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Soporte
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {isSuperAdmin
                  ? "Dudas de las agencias sobre la plataforma."
                  : "Dudas sobre la plataforma. Para incidencias de tus clientes, usa Tickets."}
              </Typography>
            </Box>
            {!isSuperAdmin && (
              <Button onClick={() => setIsCreateOpen(true)} sx={{ width: "100%", mb: 1.5 }}>
                <Plus size={16} style={{ marginRight: 6 }} />
                Nueva consulta
              </Button>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                height: 40,
                border: `1px solid ${neutrals.gray[200]}`,
                borderRadius: "8px",
              }}
            >
              <Search size={16} color={theme.palette.text.secondary} />
              <InputBase
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Buscar..."
                sx={{ flex: 1, fontSize: 14 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 0.5, mt: 1.5, flexWrap: "nowrap" }}>
              {FILTERS.map((filter) => {
                const active = statusFilter === filter.value;
                return (
                  <Box
                    key={filter.value}
                    component="button"
                    type="button"
                    onClick={() => setStatusFilter(filter.value)}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      px: 1,
                      py: 0.25,
                      fontSize: 12,
                      lineHeight: "20px",
                      whiteSpace: "nowrap",
                      borderRadius: "999px",
                      border: `1px solid ${active ? neutrals.gray[900] : neutrals.gray[200]}`,
                      backgroundColor: active ? neutrals.gray[900] : "transparent",
                      color: active ? "#fff" : "text.primary",
                    }}
                  >
                    {filterLabel(filter)}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
            {isLoadingList ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={24} />
              </Box>
            ) : tickets.length === 0 ? (
              <Box sx={{ px: 3, py: 6, textAlign: "center" }}>
                <Typography color="text.secondary">
                  {search || statusFilter !== "all"
                    ? "No hay consultas con estos filtros."
                    : isSuperAdmin
                      ? "No hay consultas de soporte."
                      : "Aún no has enviado ninguna consulta. ¿Tienes alguna duda sobre la plataforma? Escríbenos."}
                </Typography>
              </Box>
            ) : (
              tickets.map((ticket) => (
                <TicketListItem
                  key={ticket.id}
                  ticket={ticket}
                  selected={ticket.id === selectedId}
                  onSelect={setSelectedId}
                  showAgency={isSuperAdmin}
                />
              ))
            )}
          </Box>
        </Box>

        {/* Detalle */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: { xs: selectedId ? "flex" : "none", md: "flex" },
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {selectedId ? (
            <TicketDetail
              key={selectedId}
              ticketId={selectedId}
              currentUser={currentUser}
              interceptorReady={interceptorReady}
              onBack={() => setSelectedId(null)}
              onTicketChanged={refreshList}
              notify={notify}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                color: "text.secondary",
                px: 4,
                textAlign: "center",
              }}
            >
              <Headset size={36} />
              <Typography>Selecciona una consulta para ver la conversación.</Typography>
            </Box>
          )}
        </Box>
      </Dialog>

      <CreateIncidentModal
        variant="support"
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        currentUser={currentUser}
        onSubmit={handleCreate}
      />

      <SuccessPopup
        message={popup.message}
        isOpen={popup.open}
        onClose={() => setPopup((prev) => ({ ...prev, open: false }))}
        autoHideDuration={3000}
        error={popup.error}
      />
    </>
  );
}

export default SupportModal;
